import React, { useMemo, useState } from 'react';
import AuthLayout from './components/AuthLayout';
import OAuthLoginButtons from './components/OAuthLoginButtons';
import { useAuth } from './utils/useAuth';
import { ENABLE_EMAIL_AUTH, HAS_SOCIAL_AUTH } from './utils/authConfig';

const LOGIN_COPY = {
  title: 'Log in',
  subtitle: 'Continue to jobs, GPU instances, inference, and billing.'
};

function Login() {
  const auth = useAuth();
  const [formValue, setFormValue] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const getReturnTo = () => {
    try {
      const v = sessionStorage.getItem('postLoginRedirect');
      if (v && typeof v === 'string') return v.startsWith('/') ? v : `/${v}`;
    } catch (_) {
      /* ignore */
    }
    return '/jobs';
  };

  const handleEmailLogin = async () => {
    setErrors({});
    setErrorMessage('');
    if (!formValue.email || !formValue.email.includes('@')) {
      setErrors({ email: 'Valid email is required' });
      return;
    }
    if (!formValue.password) {
      setErrors({ password: 'Password is required' });
      return;
    }
    setLoading(true);
    try {
      if (auth.loginWithRedirect) {
        auth.loginWithRedirect({
          authorizationParams: {
            connection: 'Username-Password-Authentication',
            login_hint: formValue.email
          },
          appState: { returnTo: getReturnTo() },
          screen_hint: 'login'
        });
      } else {
        throw new Error('Auth0 not configured');
      }
    } catch (err) {
      setErrorMessage(err.message || 'Login failed');
      setLoading(false);
    }
  };

  const handleOAuthLogin = (connection) => {
    if (!connection) return;
    if (auth.loginWithRedirect) {
      auth.loginWithRedirect({
        authorizationParams: { connection },
        appState: { returnTo: getReturnTo() }
      });
    }
  };

  const loginFormStyles = useMemo(() => {
    const mk = (theme, darkMode) => {
      const buttonBase = {
        appearance: 'none',
        border: `1px solid ${theme.border2}`,
        borderRadius: 12,
        padding: '10px 14px',
        fontSize: 14,
        fontWeight: 600,
        lineHeight: 1,
        color: theme.text,
        background: 'transparent',
        cursor: 'pointer'
      };
      const inputBase = {
        width: '100%',
        background: theme.inputBg,
        border: `1px solid ${theme.border2}`,
        color: theme.text,
        borderRadius: 12,
        padding: '12px 14px',
        outline: 'none',
        fontSize: 14,
        boxSizing: 'border-box'
      };
      return {
        formTitle: {
          margin: 0,
          fontSize: 22,
          fontWeight: 950,
          letterSpacing: '-0.03em'
        },
        formSubtitle: {
          margin: '8px 0 0',
          color: theme.muted,
          fontSize: 14,
          lineHeight: 1.65
        },
        alert: {
          marginTop: 14,
          border: `1px solid ${theme.alertBorder}`,
          background: theme.alertBg,
          color: theme.alertText,
          borderRadius: 14,
          padding: '10px 12px',
          fontSize: 13,
          lineHeight: 1.5
        },
        form: {
          marginTop: 16,
          display: 'grid',
          gap: 12
        },
        label: {
          display: 'grid',
          gap: 8,
          fontSize: 13,
          fontWeight: 700,
          color: theme.text
        },
        input: inputBase,
        inputError: {
          ...inputBase,
          borderColor: 'rgba(248, 113, 113, 0.55)'
        },
        fieldError: {
          marginTop: 6,
          color: theme.fieldErrorColor,
          fontSize: 12,
          lineHeight: 1.35
        },
        btnPrimary: {
          ...buttonBase,
          borderColor: darkMode ? 'rgba(96, 165, 250, 0.35)' : 'rgba(59, 130, 246, 0.4)',
          background: darkMode
            ? 'linear-gradient(180deg, rgba(96, 165, 250, 0.24), rgba(59, 130, 246, 0.16))'
            : 'linear-gradient(180deg, #3B82F6, #2563EB)',
          color: theme.btnPrimaryColor,
          width: '100%',
          padding: '12px 14px'
        },
        divider: {
          margin: '14px 0',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          color: theme.muted2,
          fontSize: 12,
          fontWeight: 700
        },
        dividerLine: {
          flex: 1,
          height: 1,
          background: theme.border
        },
        oauthGrid: {
          display: 'grid',
          gap: 10
        },
        oauthBtn: {
          ...buttonBase,
          width: '100%',
          padding: '12px 14px',
          background: theme.oauthBtnBg,
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        },
        oauthBtnContent: {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10
        },
        oauthIconWrap: {
          width: 18,
          height: 18,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.text
        },
        subtleLinkBtn: {
          ...buttonBase,
          width: '100%',
          marginTop: 12,
          background: 'transparent',
          borderColor: theme.border,
          color: theme.muted
        }
      };
    };
    return mk;
  }, []);

  return (
    <AuthLayout
      brandTitle="All Completed Tuning"
      navRightSlot={(shellStyles) => (
        <>
          <button type="button" style={shellStyles.btnGhost} onClick={() => { window.location.hash = '#/'; }}>
            Home
          </button>
          {ENABLE_EMAIL_AUTH ? (
            <button type="button" style={shellStyles.btnPrimary} onClick={() => { window.location.hash = '#/signup'; }}>
              Sign up
            </button>
          ) : null}
        </>
      )}
    >
      {({ theme, darkMode, shellStyles }) => {
        const fs = loginFormStyles(theme, darkMode);
        const oauthStyles = {
          oauthGrid: fs.oauthGrid,
          oauthBtn: fs.oauthBtn,
          oauthBtnContent: fs.oauthBtnContent,
          oauthIconWrap: fs.oauthIconWrap
        };
        const cardStyle = {
          width: '100%',
          maxWidth: 460,
          margin: '0 auto',
          padding: 28,
          background: theme.cardBg,
          border: `1px solid ${theme.border}`,
          borderRadius: 20,
          boxShadow: theme.shadow
        };
        return (
          <div style={shellStyles.pageInner}>
            <div style={cardStyle} aria-label="Login form">
              <h1 style={fs.formTitle}>{LOGIN_COPY.title}</h1>
              <p style={fs.formSubtitle}>{LOGIN_COPY.subtitle}</p>

              {errorMessage ? <div style={fs.alert}>{errorMessage}</div> : null}

              {ENABLE_EMAIL_AUTH ? (
                <form
                  style={fs.form}
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleEmailLogin();
                  }}
                >
                  <div>
                    <label style={fs.label} htmlFor="login-email">
                      Email
                      <input
                        id="login-email"
                        autoComplete="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formValue.email}
                        onChange={(e) => setFormValue((v) => ({ ...v, email: e.target.value }))}
                        style={errors.email ? fs.inputError : fs.input}
                        disabled={loading}
                      />
                    </label>
                    {errors.email ? <div style={fs.fieldError}>{errors.email}</div> : null}
                  </div>

                  <div>
                    <label style={fs.label} htmlFor="login-password">
                      Password
                      <input
                        id="login-password"
                        autoComplete="current-password"
                        type="password"
                        placeholder="Password"
                        value={formValue.password}
                        onChange={(e) => setFormValue((v) => ({ ...v, password: e.target.value }))}
                        style={errors.password ? fs.inputError : fs.input}
                        disabled={loading}
                      />
                    </label>
                    {errors.password ? <div style={fs.fieldError}>{errors.password}</div> : null}
                  </div>

                  <button type="submit" style={fs.btnPrimary} disabled={loading}>
                    {loading ? 'Signing in…' : 'Log in'}
                  </button>
                </form>
              ) : null}

              {ENABLE_EMAIL_AUTH && HAS_SOCIAL_AUTH ? (
                <div style={fs.divider} aria-hidden="true">
                  <div style={fs.dividerLine} />
                  <span>Or continue with</span>
                  <div style={fs.dividerLine} />
                </div>
              ) : null}

              {HAS_SOCIAL_AUTH ? (
                <OAuthLoginButtons
                  onOAuthLogin={handleOAuthLogin}
                  loading={loading}
                  styles={oauthStyles}
                  mutedColor={theme.muted}
                />
              ) : null}

              {ENABLE_EMAIL_AUTH ? (
                <button
                  type="button"
                  style={fs.subtleLinkBtn}
                  onClick={() => { window.location.hash = '#/signup'; }}
                  disabled={loading}
                >
                  Don&apos;t have an account? Sign up
                </button>
              ) : null}
            </div>
          </div>
        );
      }}
    </AuthLayout>
  );
}

export default Login;
